import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import FileUpload from '../components/FileUpload';
import Toast from '../UI/Toast';
import { loadData } from '../services/codeDataStore';
import { DEFAULT_SUBMISSION_CHECKLIST, checklistFromText } from '../data/submissionChecklist';
import { 
  FaEnvelope, 
  FaPhoneAlt, 
  FaCheckCircle, 
  FaPaperPlane
} from 'react-icons/fa';

// Country codes for the mobile number. India: exactly 10 digits; elsewhere 6–14 digits.
const COUNTRY_CODES = [
  { code: '+91', label: 'India' },
  { code: '+977', label: 'Nepal' },
  { code: '+880', label: 'Bangladesh' },
  { code: '+94', label: 'Sri Lanka' },
  { code: '+92', label: 'Pakistan' },
  { code: '+975', label: 'Bhutan' },
  { code: '+971', label: 'UAE' },
  { code: '+966', label: 'Saudi Arabia' },
  { code: '+974', label: 'Qatar' },
  { code: '+965', label: 'Kuwait' },
  { code: '+968', label: 'Oman' },
  { code: '+973', label: 'Bahrain' },
  { code: '+1', label: 'USA / Canada' },
  { code: '+44', label: 'UK' },
  { code: '+61', label: 'Australia' },
  { code: '+64', label: 'New Zealand' },
  { code: '+65', label: 'Singapore' },
  { code: '+60', label: 'Malaysia' },
  { code: '+49', label: 'Germany' },
  { code: '+33', label: 'France' },
  { code: '+39', label: 'Italy' },
  { code: '+31', label: 'Netherlands' },
  { code: '+41', label: 'Switzerland' },
  { code: '+55', label: 'Brazil' },
  { code: '+27', label: 'South Africa' },
  { code: '+234', label: 'Nigeria' },
  { code: '+254', label: 'Kenya' },
];

const mobileRule = (countryCode) =>
  countryCode === '+91'
    ? { min: 10, max: 10, message: 'Mobile number must be exactly 10 digits.' }
    : { min: 6, max: 14, message: 'Mobile number must be 6 to 14 digits.' };

const Upload = () => {
  const location = useLocation();
  const fileUploadRef = useRef(null);

  // Parse query params for prefill
  const getPrefill = () => {
    const params = new URLSearchParams(location.search);
    return {
      firstName: params.get('firstName') || '',
      middleName: params.get('middleName') || '',
      lastName: params.get('lastName') || '',
      countryCode: params.get('countryCode') || '+91',
      mobile: params.get('mobile') || '',
      email: params.get('email') || '',
      address: params.get('address') || '',
    };
  };

  const [formData, setFormData] = useState(getPrefill());
  const [mobileError, setMobileError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [declared, setDeclared] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [submitted, setSubmitted] = useState(null); // { submissions: [{ trackingId, fileName }], emailSent } after submitting
  const [checklistItems, setChecklistItems] = useState(DEFAULT_SUBMISSION_CHECKLIST);

  // Checklist is edited in Admin -> Site Settings
  useEffect(() => {
    loadData('site', null).then((site) => setChecklistItems(checklistFromText(site?.submissionChecklist)));
  }, []);

  // Toast state
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'error'
  });

  useEffect(() => {
    setFormData(getPrefill());
    // eslint-disable-next-line
  }, [location.search]);

  const showToast = (message, type = 'error') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const clearForm = () => {
    setFormData({
      firstName: '',
      middleName: '',
      lastName: '',
      countryCode: '+91',
      mobile: '',
      email: '',
      address: ''
    });
    setMobileError('');
    setDeclared(false);
    if (fileUploadRef.current) {
      fileUploadRef.current.removeFile();
    }
    setIsSubmitting(false);
    setFormKey(prev => prev + 1);
    setTimeout(() => {
      hideToast();
    }, 4000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile' || name === 'countryCode') {
      const next = { ...formData, [name]: value };
      const rule = mobileRule(next.countryCode);
      if (name === 'mobile' && (!/^\d*$/.test(value) || value.length > rule.max)) return;
      if (name === 'countryCode' && next.mobile.length > rule.max) next.mobile = next.mobile.slice(0, rule.max);
      setFormData(next);
      const len = next.mobile.length;
      setMobileError(len !== 0 && (len < rule.min || len > rule.max) ? rule.message : '');
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.mobile.trim() || !formData.email.trim() || !formData.address.trim()) {
      showToast('Please fill all required author and contact fields.', 'error');
      return;
    }

    const rule = mobileRule(formData.countryCode);
    if (formData.mobile.length < rule.min || formData.mobile.length > rule.max) {
      setMobileError(rule.message);
      showToast('Please enter a valid mobile number.', 'error');
      return;
    }

    if (!fileUploadRef.current || !fileUploadRef.current.selectedFile) {
      showToast('Please select at least one manuscript file to upload.', 'error');
      return;
    }

    if (!declared) {
      showToast('Please confirm the author declaration before submitting.', 'warning');
      return;
    }

    setIsSubmitting(true);
    showToast('Uploading manuscript files...', 'warning');

    try {
      const uploadResult = await fileUploadRef.current.uploadFile();
      if (uploadResult && uploadResult.success && uploadResult.data) {
        const { emailSent, submissions } = uploadResult.data;
        const ids = submissions.map((sub) => sub.trackingId).join(', ');
        clearForm();
        setSubmitted({ submissions, emailSent });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const what = submissions.length > 1 ? `${submissions.length} manuscripts submitted! Tracking IDs ${ids}.` : `Manuscript submitted! Tracking ID ${ids}.`;
        const emailMessage = emailSent
          ? `${what} A confirmation email has been sent to you.`
          : `${what} Please note ${submissions.length > 1 ? 'them' : 'it'} down.`;
        setTimeout(() => {
          showToast(emailMessage, 'success');
        }, 150);
      } else {
        showToast(uploadResult?.error || 'Manuscript upload failed. Please try again or contact editor@aimjhm.com or domus.publications@gmail.com.', 'error');
      }
    } catch (err) {
      console.error('Submit manuscript error:', err);
      showToast('Submission error encountered. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="bg-[#FAF9F6] min-h-screen text-gray-800 font-sans pb-16">
      {/* Breadcrumb Bar */}
      <div className="bg-white py-3.5 px-4 sm:px-8 text-xs text-gray-500 shadow-xs">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="text-[#03478E] hover:text-[#023366] hover:underline font-medium">
            Home
          </Link>
          <span>&gt;</span>
          <span className="font-semibold text-gray-700">Submit Manuscript</span>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        
        {/* Header Block matching the new academic UI standard */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-2">
          <div>
            {/* <span className="text-xs font-bold text-[#03478E] uppercase tracking-widest block mb-1">
              ONLINE SUBMISSION PORTAL
            </span> */}
            <h1 className="page-title">
              Submit Your Manuscript
            </h1>
            {/* <p className="text-base text-gray-600 font-medium mt-1">
              Fast, double-blind peer-reviewed evaluation for homoeopathic research and clinical case reports
            </p> */}
          </div>

          {/* <div className="flex items-center gap-2">
            <Link
              to="/tracking"
              className="inline-flex items-center gap-2 text-xs font-bold text-[#03478E] hover:text-white bg-blue-50 hover:bg-[#03478E] px-5 py-2.5 rounded-full transition-all duration-200 shadow-2xs"
            >
              <FaSearch className="text-xs" />
              <span>Track Submission</span>
            </Link>
          </div> */}
        </div>

        <div>
          <main className="bg-white rounded-3xl p-8 sm:p-12 shadow-sm space-y-8">
            {submitted && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-start gap-3">
                <FaCheckCircle className="text-emerald-600 text-lg flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900 space-y-1">
                  <p className="font-bold">
                    Thank you! Your {submitted.submissions.length > 1 ? `${submitted.submissions.length} manuscripts have` : 'manuscript has'} been submitted.
                  </p>
                  {submitted.submissions.map((sub) => (
                    <p key={sub.trackingId}>
                      Tracking ID: <span className="font-bold tracking-wide">{sub.trackingId}</span>
                      {submitted.submissions.length > 1 && <span className="text-emerald-800"> ({sub.fileName})</span>}
                    </p>
                  ))}
                  <p className="text-sm text-emerald-800">
                    {submitted.emailSent
                      ? `A confirmation email has been sent to you${submitted.submissions.length > 1 ? ' for each manuscript' : ''}. `
                      : `Please note down your tracking ID${submitted.submissions.length > 1 ? 's' : ''}. `}
                    For any queries write to editor@aimjhm.com or domus.publications@gmail.com and mention your tracking ID.
                  </p>
                </div>
              </div>
            )}

            <form key={formKey} onSubmit={handleSubmit} className="space-y-8">
              
              {/* Corresponding Author */}
              <section className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full text-sm px-4 py-3 rounded-2xl bg-[#F8FAFC] border-none focus:outline-none focus:ring-2 focus:ring-[#03478E]/30 text-gray-800 transition"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">
                      Middle Name
                    </label>
                    <input
                      type="text"
                      name="middleName"
                      value={formData.middleName}
                      onChange={handleChange}
                      placeholder="Optional"
                      className="w-full text-sm px-4 py-3 rounded-2xl bg-[#F8FAFC] border-none focus:outline-none focus:ring-2 focus:ring-[#03478E]/30 text-gray-800 transition"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full text-sm px-4 py-3 rounded-2xl bg-[#F8FAFC] border-none focus:outline-none focus:ring-2 focus:ring-[#03478E]/30 text-gray-800 transition"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        aria-label="Country code"
                        className="w-28 flex-shrink-0 text-sm px-3 py-3 rounded-2xl bg-[#F8FAFC] border-none focus:outline-none focus:ring-2 focus:ring-[#03478E]/30 text-gray-800 transition"
                      >
                        {COUNTRY_CODES.map((c) => (
                          <option key={c.code} value={c.code}>{c.code} {c.label}</option>
                        ))}
                      </select>
                      <div className="relative flex-1 min-w-0">
                        <input
                          type="tel"
                          inputMode="numeric"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleChange}
                          required
                          maxLength={mobileRule(formData.countryCode).max}
                          className="w-full text-sm pl-10 pr-4 py-3 rounded-2xl bg-[#F8FAFC] border-none focus:outline-none focus:ring-2 focus:ring-[#03478E]/30 text-gray-800 transition"
                        />
                        <FaPhoneAlt className="absolute left-3.5 top-3.5 text-gray-400 text-xs" />
                      </div>
                    </div>
                    {mobileError && (
                      <p className="text-xs text-red-500 font-medium mt-1">
                        {mobileError}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full text-sm pl-10 pr-4 py-3 rounded-2xl bg-[#F8FAFC] border-none focus:outline-none focus:ring-2 focus:ring-[#03478E]/30 text-gray-800 transition"
                      />
                      <FaEnvelope className="absolute left-3.5 top-3.5 text-gray-400 text-xs" />
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">
                    Correspondence Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      rows={3}
                      className="w-full text-sm pl-10 pr-4 py-3 rounded-2xl bg-[#F8FAFC] border-none focus:outline-none focus:ring-2 focus:ring-[#03478E]/30 text-gray-800 transition resize-y"
                    />
                    {/* <FaMapMarkerAlt className="absolute left-3.5 top-3.5 text-gray-400 text-xs" /> */}
                  </div>
                </div>
              </section>

              {/* Manuscript Upload */}
              <section className="space-y-4">
                <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">
                  Manuscript File(s) <span className="text-red-500">*</span>
                  <span className="ml-2 normal-case tracking-normal font-normal text-gray-500"> </span>
                </label>
                <div>
                  <FileUpload 
                    ref={fileUploadRef}
                    formData={formData}
                  />
                </div>
              </section>

              {/* Step 4: Mandatory Submission Checklist */}
              <section className="space-y-3 bg-gray-50/70 p-6 rounded-3xl">
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#03478E]">
                  Submission Requirements Checklist
                </h3>
                <ul className="space-y-2.5 text-sm text-default">
                  {checklistItems.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <FaCheckCircle className="text-emerald-600 text-xs mt-0.5 flex-shrink-0" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Step 5: Author Declaration */}
              <div className="p-4 bg-blue-50/40 rounded-2xl flex items-start gap-3">
                <input
                  type="checkbox"
                  id="authorDeclaration"
                  checked={declared}
                  onChange={(e) => setDeclared(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded text-[#03478E] focus:ring-[#03478E] cursor-pointer"
                />
                <label htmlFor="authorDeclaration" className="text-sm text-default leading-relaxed cursor-pointer select-none">
                  I confirm that this manuscript is original, has not been published elsewhere, is not currently under consideration by another journal, and all co-authors(if any) have approved this submission in accordance with Research publishing standards.
                </label>
              </div>

              {/* Submit Action */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 px-8 rounded-full font-bold text-sm uppercase tracking-wider transition-all duration-200 shadow-md hover:shadow-xl ${
                    !isSubmitting
                      ? 'bg-[#03478E] hover:bg-[#023366] text-white cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Uploading & Processing Submission...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span>Submit Manuscript for Peer Review</span>
                      <FaPaperPlane className="text-xs" />
                    </div>
                  )}
                </button>
              </div>

            </form>
          </main>


        </div>
      </div>

      {/* Toast Notification */}
      {toast.isVisible && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={hideToast} 
          isVisible={toast.isVisible}
        />
      )}
    </div>
  );
};

export default Upload;